import CircledArrowDownIcon from '@assets/icons/circled-arrow-down.svg';
import i18n from '@/utils/i18n';
import PrinterIcon from '@assets/icons/printer.svg';
import React from 'react';
import ScanOrderIcon from '@assets/icons/scan-order.svg';
import {
  APP_ROUTES,
  CATALOG_ROUTES,
  ORDER_GUIDE_ROUTES,
} from '@/navigators/routes';
import {
  AppStackParams,
  CatalogStackParams,
  OrderGuideStackParams,
} from '@/navigators/types';
import {
  CompositeNavigationProp,
  useNavigation,
} from '@react-navigation/native';
import { getTagPrintingTemplatesForOffline } from '@/utils/tag-printing';
import { ListOption } from '@/components/list-item';
import { StackNavigationProp } from '@react-navigation/stack';
import { SvgProps } from 'react-native-svg';
import {
  useDynamicConfigValue,
  useSupplierDynamicConfigFlag,
} from './use-dynamic-config-value';
import { useLocalScanData } from './offline-mode/use-local-scan-data';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useOfflineModeActive } from './use-offline-mode-active';
import {
  useOfflineTagBatchPrint,
  useOfflineVisibleVariantPacks,
} from './use-offline-data';
import { useUserContext } from '@/contexts/user-context';

export type ScannerListOption = ListOption<{
  disabled?: boolean;
  Icon: React.ComponentType<SvgProps>;
  onPress: () => void;
}>;

type Navigation = CompositeNavigationProp<
  StackNavigationProp<AppStackParams, APP_ROUTES>,
  CompositeNavigationProp<
    StackNavigationProp<OrderGuideStackParams, ORDER_GUIDE_ROUTES.Scan>,
    StackNavigationProp<CatalogStackParams, CATALOG_ROUTES.Scan>
  >
>;

export enum ScannerOperation {
  PlaceOrder = 'PLACE_ORDER',
  RequestCredit = 'REQUEST_CREDIT',
  TagPrintBatch = 'TAG_PRINT_BATCH',
}

export const useScannerOperations = (
  baseRoute: typeof CATALOG_ROUTES | typeof ORDER_GUIDE_ROUTES,
) => {
  const {
    isGuestMode,
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID, showCreditRequest },
  } = useUserContext();
  const { showBarcodeScanner } = useNewOrderContext();

  const { tagBatchPrintData: offlineTagBatchPrintData } =
    useOfflineTagBatchPrint(restaurantUUID, supplierUUID);
  const { data: offlineVisibleVariantPackData } = useOfflineVisibleVariantPacks(
    restaurantUUID,
    supplierUUID,
  );

  const { isLoaded: localScanIsLoaded, hasData: localScanHasData } =
    useLocalScanData();

  const isOfflineModeActive = useOfflineModeActive();
  const tagPrintingConfig = useDynamicConfigValue('tag_printing_config');
  const localScanUXEnabled = useSupplierDynamicConfigFlag(
    'on_device_scanning_config',
    'local_scan_ux_enabled',
  );

  const { navigate } = useNavigation<Navigation>();

  // can print offline only when visible VP data available AND there are templates with offline support
  const canPrintWhenOffline =
    !offlineTagBatchPrintData?.templates || !offlineVisibleVariantPackData
      ? false
      : getTagPrintingTemplatesForOffline(
          Object.values(offlineTagBatchPrintData.templates),
        ).length > 0;

  const localScanWithData =
    localScanUXEnabled && localScanIsLoaded && localScanHasData;

  // true when either full offline VP data or local scan data is available
  const hasOfflineScanData =
    Boolean(offlineVisibleVariantPackData) || localScanWithData;

  if (
    isGuestMode ||
    (!showBarcodeScanner && !(isOfflineModeActive && hasOfflineScanData))
  ) {
    return [];
  }

  const batchPrintTagsDisabled = isOfflineModeActive && !canPrintWhenOffline;
  const createAnOrderDisabled = isOfflineModeActive && !hasOfflineScanData;

  const operations = [];
  if (showCreditRequest) {
    operations.push({
      metadata: {
        Icon: CircledArrowDownIcon,
        onPress: () => {
          navigate(baseRoute.CreditRequestScan);
        },
      },
      title: i18n.t('credit_request_scan'),
      value: ScannerOperation.RequestCredit,
    });
  }

  if (tagPrintingConfig?.batch_tag_printing_enabled) {
    operations.push({
      metadata: {
        disabled: batchPrintTagsDisabled,
        Icon: PrinterIcon,
        onPress: () => {
          if (batchPrintTagsDisabled) {
            return;
          }
          navigate(baseRoute.TagPrintBatch);
        },
      },
      title: i18n.t('batch_print_tags'),
      value: ScannerOperation.TagPrintBatch,
    });
  }

  return [
    ...operations,
    {
      metadata: {
        disabled: createAnOrderDisabled,
        Icon: ScanOrderIcon,
        onPress: () => {
          if (createAnOrderDisabled) {
            return;
          }
          navigate(baseRoute.Scan);
        },
      },
      title: i18n.t('create_an_order'),
      value: ScannerOperation.PlaceOrder,
    },
  ];
};
